<template>
  <div
    ref="nsHero"
    :class="[
      $style.container,
      {
        [$style.leftAlign]: scrimAlign === 'left',
        [$style.rightAlign]: scrimAlign === 'right'
      }
    ]"
  >
    <div
      :class="[$style.backgroundImage, { [$style.fullBleed]: fullBleed }]"
      :style="backgroundImageStyle"
    />
    <div
      :class="[
        $style.textbox,
        {
          [$style.dark]: scrimTextColor === 'dark',
          [$style.light]: scrimTextColor === 'light'
        }
      ]"
    >
      <div :class="$style.scrim" :style="scrimStyle">
        <h2
          v-if="headlineText"
          ref="header"
          :class="[
            $style.header,
            {
              [$style.titleLarge]: headlineFontFamily === 'Proxima Nova',
              [$style.titleDecor]: headlineFontFamily === 'Freeland'
            }
          ]"
          v-html="headlineText"
        />
        <p v-if="bodyText" :class="$style.bodyText" v-html="bodyText" />
        <p v-if="ctaLabel" :class="$style.ctaMobile">
          {{ ctaLabel }}
        </p>
        <button
          v-if="ctaType === 'button' && ctaLabel"
          :class="[$style.ctaButton, buttonClass]"
          @click="processHeroClick"
        >
          <NsIcon
            v-if="isVideo"
            :class="$style.videoIcon"
            icon-name="icon-play-outline"
          />
          {{ ctaLabel }}
        </button>
        <NsExpressiveLink
          v-if="ctaType === 'expressive-link' && ctaLabel"
          :class="$style.expressiveLink"
          :invert="scrimTextColor === 'light' || ctaLinkTheme === 'inverted'"
          @click="processHeroClick"
        >
          {{ ctaLabel }}
        </NsExpressiveLink>
      </div>
    </div>
    <div :class="$style.mobileCoverLink" @click="processHeroClick" />
    <NsLightbox
      v-if="isVideo"
      :open="lightboxOpen"
      :video-src="ctaUrl"
      :video-src-tags="ctaVideoSrcTags"
      @close="handleCloseLightbox"
    />
  </div>
</template>

<script>
import { NsExpressiveLink } from "@nuskin/design-components";
import { NsIcon } from "@nuskin/ns-icon";
import NsLightbox from "./NsLightbox.vue";
import { events } from "@nuskin/ns-util";

import { getUrlWithExtention } from "@nuskin/ns-common-lib";

export default {
  name: "NsHero",
  components: { NsExpressiveLink, NsLightbox, NsIcon },
  props: {
    /**
     * A set of images that will dynamically be set for mobile, tablet and desktop.
     * You must specify all three.
     */
    adaptiveImageSet: {
      type: Object,
      default: function() {
        return {};
      }
    },
    /**
     * Sets the background color of the container and the scrim.
     * Converts hex values to RGB so the scrim can be transparent
     * but you can also use rgb or rgba values.
     */
    backgroundColor: {
      type: String,
      default: "#FFFFFF"
    },
    /**
     * Sets the body copy
     */
    bodyText: {
      type: String,
      default: ""
    },
    /**
     * The button/link label
     */
    ctaLabel: {
      type: String,
      default: ""
    },
    /**
     * The style of the cta. Options are: button, expressive-link.
     * @values button, expressive-link
     */
    ctaType: {
      type: String,
      validator: value => ["button", "expressive-link"].includes(value),
      default: "button"
    },
    /**
     * If the cta is a button this sets the button's theme. See themes in nuskin.design docs for options.
     */
    ctaButtonTheme: {
      type: String,
      default: ""
    },
    /**
     * Use the inverted ns-expressive-link. Options are: inverted.
     * @values inverted
     */
    ctaLinkTheme: {
      type: String,
      validator: value => ["inverted"].includes(value),
      default: ""
    },
    /**
     * Either the destination url or a video url (videos will open in lightbox)
     */
    ctaUrl: {
      type: String,
      default: ""
    },
    /**
     * For videos, source tags can be used instead of ctaUrl
     */
    ctaVideoSrcTags: {
      type: String,
      default: ""
    },
    /**
     * Forces the background image's width to bleed the parent container and fill the screen's width
     */
    fullBleed: Boolean,
    /**
     * Sets the font-family for the headline. Options are: Proxima Nova, Freeland
     * @values Proxima Nova, Freeland
     */
    headlineFontFamily: {
      type: String,
      validator: value => ["Proxima Nova", "Freeland"].includes(value),
      default: "Proxima Nova"
    },
    /**
     * Sets the headline copy
     */
    headlineText: {
      type: String,
      default: ""
    },
    /**
     * Will hide the background scrim even if a color is provided
     */
    hideBackgroundScrim: Boolean,
    /**
     * Determines how the scrim should be aligned with the container. Options are: center, left, right.
     * @values center, left, right
     */
    scrimAlign: {
      type: String,
      validator: value => ["center", "left", "right"].includes(value),
      default: "left"
    },
    /**
     * Sets the theme of the scrim copy for use on either dark images or light images. Options are: dark, light.
     * @values dark, light
     */
    scrimTextColor: {
      type: String,
      validator: value => ["dark", "light"].includes(value),
      default: "dark"
    },
    /**
     * Analytics id for click event
     */
    analyticsId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      lightboxOpen: false
    };
  },
  computed: {
    buttonClass() {
      if (this.ctaButtonTheme) {
        return this.ctaButtonTheme;
      } else {
        if (this.isVideo) {
          let className =
            this.scrimTextColor === "light" ? "inverse-solid" : "primary-solid";

          // This is irrevelant. Regular button doesn't show at this size anyway
          if (this.$mq === "phablet" || this.$mq === "mobile") {
            className += " short";
          }
          return className;
        }
        return this.scrimTextColor === "light"
          ? "inverse-ghost short"
          : "primary-ghost short";
      }
    },
    imageUrl() {
      if (this.$mq === "desktop" || this.$mq === "desktopHD") {
        return this.adaptiveImageSet.desktop || "";
      } else if (this.$mq === "tablet") {
        return this.adaptiveImageSet.tablet || "";
      } else {
        return this.adaptiveImageSet.mobile || "";
      }
    },
    isVideo() {
      const vidExts = ["mov", "avi", "wmv", "flv", "3gp", "mp4", "mpg", "webm"];
      return vidExts.some(ext => this.ctaUrl.endsWith(`.${ext}`));
    },
    backgroundImageStyle() {
      return {
        backgroundImage: `url(${this.imageUrl})`,
        ...(!!this.backgroundColor && {
          backgroundColor: this.getColor(this.backgroundColor)
        })
      };
    },
    scrimStyle() {
      return {
        ...(!!this.backgroundColor && {
          backgroundColor: this.getColor(this.backgroundColor, true)
        }),
        ...(this.hideBackgroundScrim && { backgroundColor: "transparent" })
      };
    }
  },
  watch: {
    headlineFontFamily() {
      // wait for new font to take effect
      setTimeout(() => {
        this.resizeHeaderText();
      }, 100);
    }
  },
  mounted() {
    window.addEventListener("resize", () => {
      this.resizeHeaderText();
    });
    this.resizeHeaderText();
  },
  methods: {
    /**
     * Closes lightbox
     */
    handleCloseLightbox() {
      this.lightboxOpen = false;
    },
    /**
     * Fires analytics event before navigation
     */
    processHeroClick() {
      if (this.analyticsId) {
        events.publish(events.shop.NS_HERO_CLICKED, { data: this.analyticsId });
      }

      if (this.isVideo) {
        this.lightboxOpen = true;
      } else {
        window.location.href = getUrlWithExtention(this.ctaUrl);
      }
    },
    /**
     * Resizes the scrim text to fit in the scrim container
     */
    resizeHeaderText() {
      const lineHeight =
        this.headlineFontFamily === "Freeland" ? "1" : "normal";
      resizeTextToFit(this.$refs.header, 10, true);

      function resizeTextToFit(el, minSize = 10, checkHeight = false) {
        el.style.removeProperty("font-size");
        el.style.removeProperty("line-height");
        resize(el, minSize, checkHeight);

        function resize(el, minSize, checkHeight) {
          let shouldResize = el.scrollWidth > el.offsetWidth;

          // if width is fine, check height if requested
          if (checkHeight && !shouldResize) {
            const parentEl = el.parentElement;
            if (parentEl) {
              shouldResize = parentEl.scrollHeight > parentEl.offsetHeight;
            }
          }

          // if element width is greater (or optionally taller) than container, reduce fontsize
          if (shouldResize) {
            const styles = window.getComputedStyle(el);
            const newFontSize =
              parseInt(styles.getPropertyValue("font-size").slice(0, -2)) - 1;
            el.style["font-size"] = newFontSize + "px";
            el.style["line-height"] = lineHeight;

            if (newFontSize > minSize) {
              resize(el, minSize, checkHeight);
            }
          }
        }
      }
    },

    /**
     * Removes or adds the alpha channel to a color
     * Accepts a hex, rgb or rgba value
     */
    getColor(value, includeAlpha) {
      let color = value || "#ffffff";

      // hex to rgb
      if (color.includes("#")) {
        const vals = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
        if (vals) {
          color = `rgb(
            ${parseInt(vals[1], 16)},
            ${parseInt(vals[2], 16)},
            ${parseInt(vals[3], 16)}
          )`;
        }
      }

      // get r,g,b,(a) values
      const vals = color.replace(/[^\d.,]/g, "").split(",");
      let alpha = vals[3] || ".85";

      // create new rgba vars
      const colorWithAlpha = `rgba(${vals[0]}, ${vals[1]}, ${vals[2]}, ${alpha})`;
      const colorWithoutAlpha = `rgb(${vals[0]}, ${vals[1]}, ${vals[2]})`;

      return includeAlpha ? colorWithAlpha : colorWithoutAlpha;
    }
  }
};
</script>

<style lang="scss" module>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/other";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";
@use "~@nuskin/ns-core-styles/src/scss/globalVariables";

$mobile-height: 250px;
$desktop-height: 450px;

.container,
.container * {
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
}

.container {
  position: relative;
  width: 100%;
  height: $mobile-height;

  &.leftAlign {
    .backgroundImage {
      background-position: right;
    }

    .textbox {
      justify-content: flex-start;
    }
  }

  &.rightAlign {
    .backgroundImage {
      background-position: left;
    }

    .textbox {
      justify-content: flex-end;
    }
  }
}

.backgroundImage {
  position: absolute;
  width: 100%;
  height: $mobile-height;
  background-color: rgba(255, 255, 255, 1);
  background-repeat: no-repeat;
  background-size: auto $mobile-height;
  background-position: center;
  transition: background-image 1s ease-in;
  overflow: hidden;

  &.fullBleed {
    width: 100vw;
    left: calc((100vw - 100%) / -2);
  }
}

.textbox {
  width: 100%;
  height: inherit;
  margin: 0 auto;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  &.dark {
    color: map.get(colors.$themeColors, color-primary-text);
  }

  &.light {
    color: white;
  }
}

.scrim {
  padding: 15px;
  width: auto;
  max-width: 50%;
  max-height: 100%;
  background-color: rgba(255, 255, 255, 0.85);
  text-align: left;
  overflow: hidden;

  // nested for specificity
  .header {
    @include global.title-large-mobile;
    margin-bottom: 10px;
    color: inherit;
  }

  .titleDecor {
    @include global.title-decor-mobile;
    padding-bottom: 0;
    line-height: 1;
  }
}

.bodyText {
  color: inherit;
  @include global.p-small;
  display: none;
}

.ctaButton svg.videoIcon {
  margin-right: 10px;
}

.ctaMobile {
  @include global.a-micro-caps;
  color: inherit;
  position: relative;
  margin-right: 10px;
  display: inline-block;
}

.ctaMobile::after {
  content: "›";
  position: absolute;
  top: -1px;
  right: -10px;
  font-size: 14px;
  line-height: 13px;
  font-weight: 100;
}

.mobileCoverLink {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  z-index: map.get(other.$otherUtils, z-secondary);
}

@media (max-width: 767px) {
  .ctaButton,
  .expressiveLink {
    display: none !important;
  }
}

@media (min-width: 768px) {
  .container {
    height: $desktop-height;
  }

  .backgroundImage {
    height: $desktop-height;
    background-size: auto $desktop-height;
  }

  .scrim {
    padding: 30px 40px;

    // nested for specificity
    .header {
      @include global.title-large-desktop;
      margin: 0 0 25px;
    }

    .titleDecor {
      @include global.title-decor-tablet;
      padding-bottom: 0;
      line-height: 1;
    }
  }

  .bodyText {
    margin: 0 0 30px;
    display: block;
  }

  .mobileCoverLink,
  .ctaMobile {
    display: none;
  }
}

@media (min-width: 1024px) {
  .container .backgroundImage,
  .container.leftAlign .backgroundImage,
  .container.rightAlign .backgroundImage {
    background-position: center;
  }

  // nested for specificity
  .scrim .textbox {
    max-width: 994px;
  }

  .scrim .titleDecor {
    @include global.title-decor-desktop;
    padding-bottom: 0;
    line-height: 1;
  }

  .bodyText {
    @include global.p-large;
  }

  .cta {
    position: relative;
  }
}

@media (min-width: 1440px) {
  .textbox {
    max-width: 1410px;
  }
}

.header > ::v-deep *,
.bodyText > ::v-deep * {
  font-size: inherit;
  line-height: inherit;
  color: inherit;
  font-family: inherit;
}
</style>
